import { useState } from "react";

import { api } from "../api";
import { useAction, useLoad } from "../useLoad";

interface Props {
  code: string;
  onJoined: (teamId: string) => void;
  onCancel: () => void;
}

/**
 * Requirement 5.11 — following an invite link **confirms before joining**.
 *
 * Joining is never a side effect of opening a URL. That is not politeness: requirement 5.12 makes
 * joining a real team leave the one you were in, so a link that joined on load would be a link that
 * silently removed somebody from their family's team. A link should never be able to do that.
 *
 * Everything shown here comes from `GET /join/{code}` (access pattern A5), which is the one team read
 * that works for someone who is not a member — that is what being invited means. It is also the only
 * way this screen can name the team before the joiner has any right to read it.
 *
 * The four states are the four answers the preview can give, and each gets different words rather than
 * a single generic confirmation:
 *
 *  - the link is dead → say so, and say what to ask for
 *  - you are already in → say so, and offer the way in rather than a dead end
 *  - joining costs you a team → name it, and name it *harder* if nobody would be left behind
 *  - design §2.7 refuses → say why and what would unblock it, without a button that cannot work
 */
export function JoinTeam({ code, onJoined, onCancel }: Props) {
  const [failure, setFailure] = useState<string | null>(null);
  const { data, loading } = useLoad(() => api.previewJoin(code), [code], setFailure);
  const { busy, run } = useAction(setFailure);

  if (loading) {
    return (
      <div className="card">
        <p className="section-title">Invitation</p>
        <p className="muted">Checking the link…</p>
      </div>
    );
  }

  if (failure || !data) {
    return (
      <div className="card">
        <p className="section-title">That link doesn't work</p>
        <p className="muted">
          {failure ?? "The link is no longer valid."} Invite links stop working when a new one is made,
          so ask whoever sent it for a fresh one.
        </p>
        <button className="btn" onClick={onCancel}>
          Carry on without joining
        </button>
      </div>
    );
  }

  const teamName = data.name || "this team";

  if (data.alreadyMember) {
    return (
      <div className="card">
        <p className="section-title">You're already in {teamName}</p>
        <p className="muted">Nothing to do — the link was for a team you're already part of.</p>
        <button className="btn primary" onClick={() => onJoined(data.teamId)}>
          Open {teamName}
        </button>
      </div>
    );
  }

  if (data.blockedReason) {
    return (
      <div className="card">
        <p className="section-title">Join {teamName}?</p>
        {/* The sentence comes from the server, so it cannot drift from what the write would refuse. */}
        <p className="muted">{data.blockedReason}</p>
        <button className="btn" onClick={onCancel}>
          Not now
        </button>
      </div>
    );
  }

  return (
    <div className="card">
      <p className="section-title">Join {teamName}?</p>
      <p className="muted">
        You'll be able to see their races, garage and history, make predictions, and upload photos and
        video.
      </p>

      {data.leaving && (
        <p className="muted warn">
          {data.leaving.lastMember ? (
            <>
              You'll leave <strong>{data.leaving.name || "your current team"}</strong>, and since nobody
              else is in it, its races and history will no longer be reachable by anyone.
            </>
          ) : (
            <>
              You'll leave <strong>{data.leaving.name || "your current team"}</strong> and lose access to
              its races and history.
            </>
          )}
        </p>
      )}

      <div className="row">
        <button
          className="btn primary"
          disabled={busy}
          onClick={() => void run(() => api.join(code).then((result) => onJoined(result.teamId)))}
        >
          {busy ? "Joining…" : `Join ${teamName}`}
        </button>
        {/* Cancel second, but never absent. Requirement 5.11 is about the choice existing. */}
        <button className="btn" disabled={busy} onClick={onCancel}>
          No thanks
        </button>
      </div>
    </div>
  );
}
